import React, { useState, useEffect } from 'react'
import {
  Card,
  Col,
  Row,
  Accordion as BootstrapAccordion,
} from 'react-bootstrap'
import { Link } from 'react-router-dom'
import ReactMarkdown from 'react-markdown'
import { useTranslation } from 'react-i18next'
import { useDispatch } from 'react-redux'
import { useHistory } from 'react-router-dom'
import FilterListIcon from '@mui/icons-material/FilterList'
import { Drawer, Typography, Checkbox, FormControlLabel } from '@mui/material'

import emptyKnowledge from '@/assets/New-images/empty-knowledge.svg'
import RichMarkdownContent from '@/components/UI/RichMarkdownContent'
import {
  KNOWLEDGE_KNOWLEDGE_CREATE_FROM_SCRATCH,
  TOOLKIT_GENERATE_QNA_UPLOAD_DOC,
} from '@/config'
import { deleteKnowledgeById, getAllKnowledge } from '@/store/knowledge/actions'
import { TwoButtonModal as KnowledgeConfirmationModal } from '@/components/UI/Common'
import Pagination from '@/components/UI/Pagination/Pagination'
import PermissionWrapper from '@/components/UI/PermissionWrapper/PermissionWrapper'
import CustomMenu from '@/components/UI/CustomMenu'
import ActionContextMenu from './ActionContextMenu'
import KnowledgeCreateFromScratch from '@/routes/Knowledge/KnowledgeCreateFromScratch/KnowledgeCreateFromScratch'
import AccordionCard from '@/components/UI/AccordionCard'
import { localDateTime } from '@/utils/formatUtils'

interface FlashCardsProps {
  allKnowledgeSuccess: any
  allIntents: any[]
  selectedNodes: string[]
  selectedGroup: any
  currentPage: number
  perPage: number
  activeKey: string | null
  query: string
  documentId?: string
  onSelectAllRows: (event: any) => void
  addToSelectedRows: (event: any, id: string) => void
  bulkAssignCategoryKnowledgeHandler: () => void
  setActiveKey: (key: string | null) => void
  setSelectedNodes: (nodes: string[]) => void
  setLoading: (loading: boolean) => void
  setCurrentPage: (page: number) => void
  setPerPage: (perPage: number) => void
  setQuery: (query: string) => void
}

const FlashCards: React.FC<FlashCardsProps> = ({
  allKnowledgeSuccess,
  allIntents,
  selectedNodes,
  selectedGroup,
  currentPage,
  perPage,
  activeKey,
  query,
  documentId,
  onSelectAllRows,
  addToSelectedRows,
  bulkAssignCategoryKnowledgeHandler,
  setActiveKey,
  setSelectedNodes,
  setLoading,
  setCurrentPage,
  setPerPage,
  setQuery,
}) => {
  const { t } = useTranslation('translation')
  const history: any = useHistory()
  const dispatch = useDispatch()

  // Modal state
  const [editMode, setEditMode] = useState(false)
  const [selectedIntent, setSelectedIntent] = useState(null)
  const [showDeleteConfirmationModal, setShowDeleteConfirmationModal] =
    useState(false)
  const [showDeletePermanentlyModal, setShowDeletePermanentlyModal] =
    useState(false)
  const [deletedRowId, setDeletedRowId] = useState<string[]>([])
  const [isTyping, setTyping] = useState(false)

  /**
   * Set debounce time for while searching in any input
   */
  useEffect(() => {
    // Added 500 as debounce time will wait for 500 miliseconds and then we will fetch intents based on query
    const delayDebounceFn = setTimeout(() => {
      if (isTyping) {
        setCurrentPage(1)
        dispatch(
          getAllKnowledge(
            3,
            1,
            perPage,
            query,
            '',
            {},
            selectedNodes.length > 0,
            documentId,
            selectedGroup?._id,
            true,
          ),
        )
        setTyping(false)
      }
    }, 500)
    return () => clearTimeout(delayDebounceFn)
  }, [query, isTyping])

  const onInputChangeHandler = (e: React.ChangeEvent<HTMLInputElement>) => {
    e.preventDefault()
    setQuery(e.target.value)
    setTyping(true)
  }

  /**
   * Used to sort data using API
   */
  const onSortHandler = (selector: string, direction: string) => {
    dispatch(
      getAllKnowledge(
        3,
        currentPage,
        perPage,
        query,
        '',
        {
          sort_field: selector === 'name' ? 'question' : selector,
          sort_order: direction === 'desc' ? 'dsc' : 'asc',
        },
        selectedNodes.length > 0,
        documentId,
        selectedGroup?._id,
        true,
      ),
    )
  }

  const handlePageChange = (page: number) => {
    setCurrentPage(page)
    dispatch(
      getAllKnowledge(
        3,
        page,
        perPage,
        query,
        '',
        {},
        selectedNodes.length > 0,
        documentId,
        selectedGroup?._id,
        true,
      ),
    )
  }

  const handlePerRowsChange = async (newPerPage: number, page: number) => {
    setPerPage(newPerPage)
    setCurrentPage(page)
    dispatch(
      getAllKnowledge(
        3,
        page,
        newPerPage,
        query,
        '',
        {},
        selectedNodes.length > 0,
        documentId,
        selectedGroup?._id,
        true,
      ),
    )
  }

  /**
   * Used to delete multiple knowledge based on IDS
   */
  const deleteConfirmed = async (
    selectedRowIds: string[],
    isArchiving: boolean = false,
  ) => {
    setLoading(true)
    await dispatch(deleteKnowledgeById(selectedRowIds, isArchiving))
    dispatch(
      getAllKnowledge(
        3,
        currentPage,
        perPage,
        query,
        '',
        {},
        selectedNodes.length > 0,
        documentId,
        selectedGroup?._id,
        true,
      ),
    )
    setLoading(false)
    setSelectedNodes([])
  }

  const editKnowledgehandler = (intent: any) => {
    const editModeCheck = !editMode
    setSelectedIntent(intent)
    setEditMode(editModeCheck)
  }

  const resetEditKnowledgehandler = (action: any) => {
    const editModeCheck = !editMode
    setSelectedIntent(null)
    setEditMode(editModeCheck)

    // From edit mode if any intent gets deleted then we have to reload data
    if (action === 'reload') {
      dispatch(
        getAllKnowledge(
          3,
          currentPage,
          perPage,
          query,
          '',
          {},
          selectedNodes.length > 0,
          documentId,
          selectedGroup?._id,
          true,
        ),
      )
    }
  }

  // Reset page to 1 when selectedGroup changes
  useEffect(() => {
    setCurrentPage(1)
  }, [selectedGroup?._id, setCurrentPage])

  useEffect(() => {
    if (selectedGroup?._id) {
      const selectedId = !documentId ? selectedGroup?._id : null

      dispatch(
        getAllKnowledge(
          3,
          currentPage,
          perPage,
          query,
          '',
          {},
          selectedNodes.length > 0,
          documentId,
          selectedId,
          true,
        ),
      )
    }
  }, [selectedGroup, currentPage, perPage])

  return (
    <>
      <KnowledgeConfirmationModal
        show={showDeletePermanentlyModal}
        title={t('Confirm Delete')}
        message={
          t('Are you sure you want to delete? Deleted Items will be ') +
          '<b class="clr-red">' +
          t('deleted permanently.') +
          '</b>'
        }
        innerHtml
        size={'lg'}
        leftButtonText={t('Delete')}
        leftButtonClassName="btn btn-outline-danger font-weight-normal mx-2"
        rightButtonText={t('Cancel')}
        rightButtonClassName="btn btn-outline-primary font-weight-normal mx-2"
        onClickLeftButton={() => {
          setShowDeletePermanentlyModal(false)
          deleteConfirmed(deletedRowId)
        }}
        onClickRightButton={() => {
          setShowDeletePermanentlyModal(false)
        }}
        onClickClose={() => setShowDeletePermanentlyModal(false)}
      />
      <KnowledgeConfirmationModal
        show={showDeleteConfirmationModal}
        title={t('Remove Knowledge')}
        message={t(
          'Would you like to archive or delete the knowledge(s) from Knowledge base?',
        )}
        size={'lg'}
        leftButtonText={t('Delete')}
        leftButtonClassName="btn btn-outline-danger font-weight-normal mx-2"
        rightButtonText={t('Archive')}
        rightButtonClassName="btn btn-primary font-weight-normal mx-2"
        onClickLeftButton={() => {
          setShowDeleteConfirmationModal(false)
          setShowDeletePermanentlyModal(true)
        }}
        onClickRightButton={() => {
          setShowDeleteConfirmationModal(false)
          deleteConfirmed(deletedRowId, true)
        }}
        onClickClose={() => setShowDeleteConfirmationModal(false)}
      />

      <div className="all-knowledge-search mb-2 tw-flex tw-flex-wrap tw-items-center tw-gap-2">
        <CustomMenu
          textTrigger={t('Sort')}
          options={[
            {
              label: t('Most Recent'),
              onClick: () => onSortHandler('updated_at', 'desc'),
            },
            {
              label: t('Ascending'),
              onClick: () => onSortHandler('name', 'asc'),
            },
            {
              label: t('Descending'),
              onClick: () => onSortHandler('name', 'desc'),
            },
          ]}
          slotProps={{
            trigger: {
              startIcon: <FilterListIcon fontSize="small" />,
              size: 'small',
              variant: 'outlined',
              sx: { padding: '8px 12px', fontWeight: 700 },
            },
          }}
        />
        <div className="web-links my-0 mr-0">
          <input
            type="text"
            className="form-control"
            placeholder={t('Search for Flash cards')}
            onChange={onInputChangeHandler}
          />
        </div>
        <ActionContextMenu
          triggerText={t('+ Add')}
          menuItems={[
            {
              text: t('Create from Scratch'),
              action: () =>
                history.push(
                  `${KNOWLEDGE_KNOWLEDGE_CREATE_FROM_SCRATCH}?group=${selectedGroup?._id}&view-all=true`,
                ),
            },
            {
              text: t('Create from Document'),
              action: () =>
                history.push(
                  `${TOOLKIT_GENERATE_QNA_UPLOAD_DOC}?group=${selectedGroup?._id}`,
                ),
            },
          ]}
        />
      </div>
      <div className="d-flex flex-wrap justify-content-between align-items-center mt-1">
        <div className="d-flex align-items-center">
          {allKnowledgeSuccess?.intents?.length > 0 && (
            <div className="mr-3">
              <FormControlLabel
                control={
                  <Checkbox
                    checked={
                      allKnowledgeSuccess &&
                      allKnowledgeSuccess.intents &&
                      allKnowledgeSuccess.intents.length > 0 &&
                      selectedNodes.length ===
                        allKnowledgeSuccess.intents.length
                    }
                    onChange={onSelectAllRows}
                    size="small"
                  />
                }
                label={t('Select all')}
              />
            </div>
          )}
          {selectedNodes && selectedNodes.length > 0 && (
            <PermissionWrapper>
              <button
                className="btn btn-suggest py-0 font-sm"
                onClick={() => bulkAssignCategoryKnowledgeHandler()}
              >
                {t('Move Selected')}
              </button>
              <button
                className="btn btn-outline-danger border-0 py-0 font-600 font-sm"
                onClick={() => {
                  setDeletedRowId(selectedNodes)
                  setShowDeleteConfirmationModal(true)
                }}
              >
                {t('Delete Selected')}
              </button>
            </PermissionWrapper>
          )}
        </div>
      </div>
      <BootstrapAccordion
        defaultActiveKey={
          allKnowledgeSuccess && allKnowledgeSuccess.intents.length > 0
            ? allKnowledgeSuccess.intents[0]._id
            : null
        }
        className="accordion-viewby "
      >
        {allKnowledgeSuccess?.intents?.map((knowledge: any) => {
          return (
            <Card
              className={activeKey === '0' ? 'active' : 'notactive'}
              key={knowledge?._id}
            >
              <AccordionCard
                id={knowledge?._id}
                title={
                  knowledge.questions && knowledge.questions.length > 0
                    ? knowledge.questions[knowledge.questions.length - 1].text
                    : ''
                }
                breadcrumbs={
                  knowledge.breadcrumbs && knowledge.breadcrumbs.length > 0
                    ? knowledge.breadcrumbs
                    : []
                }
                expanded={activeKey === knowledge?._id}
                selected={selectedNodes.indexOf(knowledge?._id) >= 0}
                onToggle={() =>
                  setActiveKey(
                    activeKey === knowledge?._id ? null : knowledge?._id,
                  )
                }
                onSelect={checked => {
                  const event = {
                    target: { checked },
                    preventDefault: () => {},
                    stopPropagation: () => {},
                  }
                  addToSelectedRows(event, knowledge?._id)
                }}
                content={
                  <>
                    <div>
                      <h3 className="clr-black mb-1">{t('Answer')}</h3>
                      <Typography>
                        {knowledge.responses &&
                        knowledge.responses.length > 0 ? (
                          <RichMarkdownContent
                            contextText={knowledge.responses[0].text}
                          />
                        ) : (
                          ''
                        )}
                      </Typography>
                    </div>
                    <Row className="mt-3">
                      <Col md={4} className="my-2 my-md-0">
                        <h3 className="clr-black mb-1">
                          {t('Generated from')}
                        </h3>
                        <Typography>{knowledge?.document_name}</Typography>
                      </Col>
                      <Col md={3} className="my-2 my-md-0">
                        <h3 className="clr-black mb-1">{t('Created by')}</h3>
                        <Typography>{knowledge?.author_email}</Typography>
                      </Col>
                      <Col md={5} className="my-2 my-md-0">
                        <h3 className="clr-black mb-1">{t('Last Updated')}</h3>
                        <Typography>
                          {localDateTime(knowledge.updated_at)}
                        </Typography>
                      </Col>
                    </Row>
                  </>
                }
                actions={[
                  {
                    label: t('Edit'),
                    variant: 'outlined',
                    color: 'primary',
                    onClick: () => editKnowledgehandler(knowledge),
                  },
                  {
                    label: t('Delete'),
                    variant: 'outlined',
                    color: 'error',
                    onClick: () => {
                      setDeletedRowId([knowledge._id])
                      setShowDeleteConfirmationModal(true)
                    },
                  },
                ]}
              />
            </Card>
          )
        })}
      </BootstrapAccordion>

      {/* empty state */}
      {allKnowledgeSuccess && allKnowledgeSuccess.intents.length === 0 && (
        <div className="empty-knowledge text-center my-2">
          <h3 className="h6 clr-grey-txt7 mb-3">
            {t('You have yet to create knowledge')}
          </h3>
          <figure className="h-auto">
            <img src={emptyKnowledge} alt="" />
          </figure>
          <div className="clr-primary font-weight-bold">
            <Link
              to={`${KNOWLEDGE_KNOWLEDGE_CREATE_FROM_SCRATCH}?group=${selectedGroup?._id}`}
            >
              {t('Click here to get started')}
            </Link>
          </div>
        </div>
      )}

      {allKnowledgeSuccess &&
        allKnowledgeSuccess.pagination &&
        allKnowledgeSuccess.intents.length > 0 && (
          <Pagination
            currentPage={currentPage}
            pagination={allKnowledgeSuccess.pagination}
            paginationPerPage={perPage}
            pageChange={handlePerRowsChange}
            onChangePage={handlePageChange}
            style={{ position: 'relative' }}
          />
        )}

      {editMode && (
        <Drawer
          anchor="right"
          open={editMode}
          onClose={() => resetEditKnowledgehandler(null)}
          slotProps={{
            paper: {
              sx: {
                width: { xs: '95%', sm: '750px' },
                maxWidth: '750px',
              },
            },
          }}
        >
          <KnowledgeCreateFromScratch
            resetEditKnowledgehandler={resetEditKnowledgehandler}
            selectedIntent={selectedIntent}
            editMode={editMode}
            viewall
          />
        </Drawer>
      )}
    </>
  )
}

export default FlashCards
